import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

interface FloatingPanelProps {
  readonly title: string;
  readonly role?: 'dialog';
  readonly ariaLabel?: string;
  readonly closeLabel: string;
  onClose: () => void;
  readonly children: ReactNode;
}

/**
 * A drawer pinned to the right edge of the viewport. It is out of the document flow, so the claims table
 * keeps its full width and stays readable behind it rather than being narrowed (US-012 AC-001).
 *
 * Deliberately not modal: nothing is dimmed and the grid stays reachable, which is the point. Focus
 * moves in on open and returns to whatever opened it on close, and Escape closes it.
 *
 * Only one drawer is ever open — see useClaimsWorkspace. Two on the same edge would cover each other.
 */
export function FloatingPanel({
  title,
  role,
  ariaLabel,
  closeLabel,
  onClose,
  children,
}: FloatingPanelProps): React.JSX.Element {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const opener = document.activeElement;
    headingRef.current?.focus();

    return (): void => {
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return (): void => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <aside
      role={role}
      aria-modal={role === 'dialog' ? false : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel === undefined ? headingId : undefined}
      className={cn(
        'border-line animate-drawer-in fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l bg-surface shadow-2xl',
        'sm:w-[400px]',
      )}
    >
      <div className="border-line flex items-start justify-between gap-4 border-b px-5 py-4">
        <h3 id={headingId} ref={headingRef} tabIndex={-1} className="text-brand-600 text-lg font-bold">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="text-muted hover:text-brand-600"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <div className="overflow-y-auto px-5 py-4">{children}</div>
    </aside>
  );
}
